import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import HelpBubble from "./HelpBubble";
import Stat from "./optimize/Stat";

const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

/**
 * Day-parting: bid-shave (default, preserves FB learning phase) vs hard-pause.
 */
export default function DaypartingPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("bid_shave");
  const [shavePct, setShavePct] = useState(40);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/optimize/dayparting");
      setData(data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const apply = async () => {
    if (!data) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/optimize/dayparting/apply", {
        hours: data.recommended_on_hours,
        mode,
        shave_pct: mode === "bid_shave" ? shavePct : undefined,
      });
      if (res.mode === "bid_shave") {
        toast.success(`Bid-shaved · −${shavePct}% across ${res.shaved_hours.length} cold hours (learning preserved)`);
      } else {
        toast.success(`Hard-paused · ${res.paused_hours.length} cold hours`);
      }
    } finally { setBusy(false); }
  };

  if (!data) return <div className="text-slate-500 text-sm p-4">Loading…</div>;

  const maxRoi = Math.max(...data.hours.map((h) => Math.abs(h.roi)));

  return (
    <div className="space-y-4" data-testid="dayparting-panel">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Avg ROI" value={fmtPct(data.avg_roi)} color={data.avg_roi > 0 ? "emerald" : "red"} />
        <Stat label="Hot hours" value={`${data.recommended_on_hours.length}h`} color="emerald" sub={`+${data.expected_lift_pct}% lift`} />
        <Stat label="Cold hours" value={`${data.recommended_off_hours.length}h`} color="red" sub={mode === "bid_shave" ? `−${shavePct}% bid` : "pause window"} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid="dayparting-mode">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Cold-hour strategy</div>
        <label className={`flex items-center gap-1.5 text-xs font-medium cursor-pointer px-2 py-1 rounded border ${mode === "bid_shave" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600"}`}>
          <input type="radio" className="sr-only" checked={mode === "bid_shave"} onChange={() => setMode("bid_shave")} data-testid="mode-bid-shave" />
          Bid-shave (recommended)
        </label>
        <label className={`flex items-center gap-1.5 text-xs font-medium cursor-pointer px-2 py-1 rounded border ${mode === "hard_pause" ? "border-red-300 bg-red-50 text-red-800" : "border-slate-200 text-slate-600"}`}>
          <input type="radio" className="sr-only" checked={mode === "hard_pause"} onChange={() => setMode("hard_pause")} data-testid="mode-hard-pause" />
          Hard pause
        </label>
        {mode === "bid_shave" && (
          <div className="flex items-center gap-1.5 ml-auto" data-testid="dayparting-shave">
            <span className="text-[10px] font-mono text-slate-500">shave</span>
            <input
              type="range" min={10} max={70} step={5}
              value={shavePct} onChange={(e) => setShavePct(parseInt(e.target.value, 10))}
              className="w-32" data-testid="dayparting-shave-slider"
            />
            <span className="text-xs font-mono font-semibold text-emerald-700 w-10 tabular-nums">−{shavePct}%</span>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">14-day ROI curve by hour <HelpBubble feature="dayparting" size="xs" /></div>
          <button
            onClick={apply}
            disabled={busy}
            data-testid="dayparting-apply-btn"
            className="bg-blue-600 text-white rounded-md px-3 py-1 text-xs font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            {mode === "bid_shave" ? `Apply bid-shave (−${shavePct}%)` : "Apply hard-pause schedule"}
          </button>
        </div>
        <div className="grid grid-cols-24 gap-px" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
          {data.hours.map((h) => {
            const height = Math.max(6, (Math.abs(h.roi) / maxRoi) * 90);
            const color = h.hot ? "bg-emerald-500" : h.cold ? "bg-red-500" : "bg-zinc-600";
            return (
              <div key={h.hour} className="flex flex-col items-center" data-testid={`dp-hour-${h.hour}`}>
                <div className="w-full h-24 flex items-end">
                  <div
                    className={`w-full rounded-t ${color} transition-all`}
                    style={{ height: `${height}%` }}
                    title={`${h.hour}:00 · ROI ${h.roi}%`}
                  />
                </div>
                <div className="text-[9px] text-slate-500 font-mono mt-1">{h.hour}</div>
                <div className={`text-[9px] font-mono ${h.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>{h.roi}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-[11px] text-slate-500 font-mono">
          Recommended ON: <span className="text-emerald-600">{data.recommended_on_hours.map((h) => `${h}:00`).join(", ") || "—"}</span>
        </div>
        <div className="text-[11px] text-slate-500 font-mono">
          Recommended OFF: <span className="text-red-600">{data.recommended_off_hours.map((h) => `${h}:00`).join(", ") || "—"}</span>
        </div>
      </div>
    </div>
  );
}
